import React, { useState } from "react";
import styled from "styled-components";
import { sendPasswordResetEmail } from "firebase/auth";
import { toast } from "react-toastify";
import { auth } from "../firebase/firebaseConfig";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    if (!email) {
      setStatus({ type: "error", message: "Please enter your email address." });
      return;
    }

    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      const actionCodeSettings = {
        url: window.location.origin + "/reset-password",
        handleCodeInApp: false,
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);

      setStatus({
        type: "success",
        message: "Password reset email sent. Please check your inbox.",
      });
      toast.success("Reset link sent!");
    } catch (err) {
      console.error("sendPasswordResetEmail error:", err);
      let msg = "Failed to send reset email. Try again later.";

      if (err.code === "auth/user-not-found") {
        msg = "No user found with this email address.";
      } else if (err.code === "auth/invalid-email") {
        msg = "Invalid email address.";
      } else if (err.code === "auth/missing-continue-uri") {
        msg = "Continue URL is missing. Please contact support.";
      } else if (err.code === "auth/unauthorized-continue-uri") {
        msg = "Continue URL domain not authorized. Check Firebase settings.";
      }

      setStatus({ type: "error", message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <h2>Forgot Password</h2>
        <p>Enter your email address and we'll send you a reset link.</p>

        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Button onClick={handleReset} disabled={loading}>
          {loading ? "Sending..." : "Send Reset Link"}
        </Button>

        {status.message && (
          <Message error={status.type === "error"}>{status.message}</Message>
        )}
      </Card>
    </Container>
  );
};

export default ForgotPassword;

/* ---------------- Styled Components ---------------- */

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: radial-gradient(circle at top left, #0b0617, #000);
  padding: 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 20px;
  padding: 32px 28px;
  width: 400px;
  max-width: 90%;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);

  h2 {
    margin-bottom: 16px;
    color: #c59fff;
    font-size: 24px;
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 14px;
    color: #ccc;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    padding: 24px;
    h2 {
      font-size: 20px;
    }
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  margin-bottom: 16px;
  border-radius: 10px;
  border: 1px solid #444;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 15px;
  box-sizing: border-box;
  outline: none;
  transition: all 0.25s;

  &::placeholder {
    color: #888;
  }

  &:focus {
    border: 1px solid #9b5cff;
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px 0;
  border: none;
  border-radius: 12px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(155, 92, 255, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 12px;
  font-size: 14px;
  text-align: center;
  color: ${({ error }) => (error ? "#f87171" : "#4ade80")};
`;
